import { CommonActions } from '@bugbug/core/actions/actions';
import { useCallback, useMemo } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { Maybe } from '@bugbug/core/types/utils';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { selectPlaybackCursorPosition } from '~/modules/uiState/uiState.selectors';

import { selectCurrentTest, selectSortedSteps } from '../../tests.selectors';

export const usePlaybackCursorPosition = () => {
  const dispatch = useAppDispatch();
  const sortedSteps = useAppSelector(selectSortedSteps);
  const cursorPosition = useAppSelector(selectPlaybackCursorPosition);
  const test = useAppSelector(selectCurrentTest);

  const keepLatestPlaybackCursorPosition = useCallback(() => {
    const cursorStepIndex = sortedSteps.findIndex(
      (step) =>
        step.id === cursorPosition?.afterStep?.id &&
        step.groupId === cursorPosition?.afterStep?.groupId,
    );
    dispatch(CommonActions.keepLatestPlaybackCursorPosition(cursorStepIndex));
  }, [cursorPosition?.afterStep?.groupId, cursorPosition?.afterStep?.id, dispatch, sortedSteps]);

  const refreshPlaybackCursorPosition = useCallback(
    (...params: Parameters<typeof CommonActions.refreshPlaybackCursorPosition>) => {
      dispatch(CommonActions.refreshPlaybackCursorPosition(...params));
    },
    [dispatch],
  );

  const calculateNewCursorPosition = useCallback(
    (afterStepId: Maybe<Step['id']>, groupId: Group['id']) => {
      const firstGroupPosition = {
        id: null,
        groupId,
        isActive: true,
      };

      // -1 if afterStepId is null
      const afterStepIndex = sortedSteps.findIndex(
        (s) => s.id === afterStepId && s.groupId === groupId,
      );

      const groupIndex = test?.groups.indexOf(groupId) ?? -1;

      const previousActiveStepIndex = sortedSteps.findLastIndex((step, stepIndex) => {
        if (!step.isActive) {
          return false;
        }

        if (afterStepId !== null) {
          return stepIndex <= afterStepIndex;
        }

        // If afterStepId is null, we need to find the step from previous groups
        const stepGroupIndex = test?.groups.indexOf(step.groupId) ?? -1;
        return stepGroupIndex < groupIndex;
      });

      const afterStep = sortedSteps[afterStepIndex] ?? firstGroupPosition;
      const previousStep = (sortedSteps[previousActiveStepIndex] as Maybe<Step>) ?? null;
      const nextStepIndex =
        afterStepId !== null && afterStepIndex < sortedSteps.length - 1
          ? afterStepIndex + 1
          : sortedSteps.findIndex(
              (step, stepIndex) =>
                test &&
                stepIndex > previousActiveStepIndex &&
                test.groups.indexOf(step.groupId) >= groupIndex,
            );

      const nextStep: Maybe<Step> = sortedSteps[nextStepIndex] ?? null;
      const nextActiveStep: Maybe<Step> =
        nextStepIndex >= 0
          ? (sortedSteps.slice(nextStepIndex).find((s) => s.isActive) ?? null)
          : null;

      return {
        afterStep,
        previousStep,
        nextStep,
        nextStepIndex,
        nextActiveStep,
      };
    },
    [sortedSteps, test],
  );

  return useMemo(
    () => ({
      refreshPlaybackCursorPosition,
      keepLatestPlaybackCursorPosition,
      calculateNewCursorPosition,
    }),
    [keepLatestPlaybackCursorPosition, refreshPlaybackCursorPosition, calculateNewCursorPosition],
  );
};
